import { useGetLanguage } from '@/hooks/useGetLanguage';
import {
  OpinionPoll,
  type OpinionPollsListParams,
  OpinionsApi,
} from '@/modules/opinions';
import { useStore } from '@/store';

import { useInfiniteQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface OpinionsContextType {
  data: any;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const OpinionsContext = createContext<OpinionsContextType | null>(null);

export const useOpinions = () => {
  const context = useContext(OpinionsContext);
  if (!context) {
    console.error('useOpinions must be used within an OpinionsProvider');
    throw new Error('useOpinions must be used within an OpinionsProvider');
  }
  return context;
};

export const OpinionPollsProvider = observer(
  ({
    initialOpinionId,
    children,
  }: {
    initialOpinionId?: string;
    children: ReactNode;
  }) => {
    const lang = useGetLanguage();
    const {
      pollsStore: { selectedTopics },
    } = useStore();
    const LIMIT = 10;
    const [injectedOpinion, setInjectedOpinion] = useState<OpinionPoll | null>(
      null,
    );

    const query = useInfiniteQuery({
      queryKey: [OpinionsApi.KEY, 'getOpinions', lang, selectedTopics.opinions],
      queryFn: async ({ pageParam = 0 }) => {
        const requestParams: OpinionPollsListParams = {
          lang,
          limit: LIMIT,
          skip: pageParam * LIMIT,
          filters: selectedTopics.opinions.length
            ? { topics: selectedTopics.opinions }
            : undefined,
        };

        return await OpinionsApi.getOpinions(requestParams);
      },
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length < LIMIT ? undefined : allPages.length,
      initialPageParam: 0,
    });

    const {
      data: rawData,
      isLoading,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    } = query;

    const flatList = useMemo(() => rawData?.pages.flat() ?? [], [rawData]);

    const opinionExists = initialOpinionId
      ? flatList.some((op) => op._id === initialOpinionId)
      : true;

    const isInjectedNeeded =
      initialOpinionId && !opinionExists && !injectedOpinion;

    useEffect(() => {
      if (!isInjectedNeeded || !initialOpinionId) return;

      OpinionsApi.getOpinionsById(initialOpinionId, lang)
        .then((opinion) => {
          setInjectedOpinion(opinion);
        })
        .catch((err) => {
          console.error('Failed to load opinion by ID:', err);
        });
    }, [initialOpinionId, lang, isInjectedNeeded]);

    const isDataReady =
      !isLoading && !!rawData && (opinionExists || !!injectedOpinion);

    const mergedData = useMemo(() => {
      if (!rawData) return { pages: [] };

      const combined = injectedOpinion
        ? [injectedOpinion, ...flatList]
        : flatList;

      const uniqueMap = new Map<string, OpinionPoll>();
      combined.forEach((op) => uniqueMap.set(op._id, op));

      return {
        pages: [Array.from(uniqueMap.values())],
      };
    }, [rawData, flatList, injectedOpinion]);

    return (
      <OpinionsContext.Provider
        value={{
          data: mergedData,
          isLoading: !isDataReady,
          fetchNextPage,
          hasNextPage,
          isFetchingNextPage,
        }}
      >
        {children}
      </OpinionsContext.Provider>
    );
  },
);
